import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import {
  attachPermissionMounted,
  detachPermissionMounted,
  roleMounted,
} from './model'
import { useRoleDetails } from './hooks'
import { Permission } from './types'
import { Guard } from '../../../../features/session/guard'
import { path } from '../../../../libs'
import { useNavigate } from 'react-router-dom'

const { Text } = Typography

export const RoleDetails: React.FC = () => {
  const navigate = useNavigate()

  const {
    role,
    inheritedRoles,
    rolePermissions,
    allPermissions,
    loading,
    permissionsLoading,
  } = useRoleDetails()

  const [selectedPermission, setSelectedPermission] = useState<
    string | undefined
  >(undefined)

  useEffect(() => {
    roleMounted()
  }, [])

  const handleAttach = () => {
    if (!selectedPermission || !role.id) return
    attachPermissionMounted({
      permissionId: selectedPermission,
      roleId: role.id,
    })
    setSelectedPermission(undefined)
  }

  const handleDetach = (permissionId: string) => {
    if (!role.id) return
    detachPermissionMounted({ permissionId, roleId: role.id })
  }

  const attachedIds = useMemo(
    () => new Set(rolePermissions.map((p) => p.id)),
    [rolePermissions],
  )

  const availableOptions = useMemo(
    () =>
      allPermissions
        .filter((p) => !attachedIds.has(p.id))
        .map((p) => ({
          value: p.id,
          label: p.description ? `${p.name} — ${p.description}` : p.name,
        })),
    [allPermissions, attachedIds],
  )

  const permissionColumns: ColumnsType<Permission> = [
    { title: 'Name', dataIndex: 'name' },
    { title: 'Description', dataIndex: 'description' },
    {
      title: 'Actions',
      fixed: 'right',
      width: 180,
      render: (item: Permission) => (
        <Guard permission="edit_permission">
          <Button
            danger
            size="small"
            onClick={() => {
              if (window.confirm(`Detach "${item.name}" from ${role.id}?`)) {
                handleDetach(item.id)
              }
            }}
          >
            Detach
          </Button>
        </Guard>
      ),
    },
  ]

  if (loading && !role.id) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    )
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card title={<strong>Role: {role.id}</strong>}>
        <div style={{ marginTop: 4 }}>
          <Text type="secondary">Description:</Text>{' '}
          {role.description || '—'}
        </div>
        {inheritedRoles.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">Inherited roles:</Text>{' '}
            <Space wrap>
              {inheritedRoles.map((r) => (
                <Tag
                  key={r}
                  color="blue"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(path.roles.details(r))}
                >
                  {r}
                </Tag>
              ))}
            </Space>
          </div>
        )}
        {role.userGroups && role.userGroups.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">User groups:</Text>{' '}
            <Space wrap>
              {role.userGroups.map((g) => (
                <Tag
                  key={g.id}
                  color="geekblue"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(path.groups.edit(g.id))}
                >
                  {g.name}
                </Tag>
              ))}
            </Space>
          </div>
        )}
      </Card>

      <Card title="Permissions">
        <Guard permission="edit_permission">
          <Row gutter={8} style={{ marginBottom: 12 }}>
            <Col flex="auto">
              <Select
                style={{ width: '100%' }}
                placeholder="Choose permission…"
                value={selectedPermission}
                onChange={setSelectedPermission}
                options={availableOptions}
                showSearch
                optionFilterProp="label"
                allowClear
              />
            </Col>
            <Col>
              <Button
                type="primary"
                disabled={!selectedPermission}
                onClick={handleAttach}
              >
                Attach
              </Button>
            </Col>
          </Row>
        </Guard>
        <Table
          dataSource={rolePermissions}
          columns={permissionColumns}
          bordered
          loading={permissionsLoading}
          rowKey="id"
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Card>
    </Space>
  )
}

export const RoleDetailsPage = (): JSX.Element => <RoleDetails />
